import React, { useEffect } from 'react';
import {
  _t, locations, createLink, isEmptyArray, resourceIconMap,
} from 'utils';
import { actions } from 'redux/actions';
import { ACTIONS_TYPES } from 'redux/helper';
import { useSelector } from 'react-redux';
import Shared from 'screens/shared';
import { CPU_FAMILY, RESOURCE_TYPE, ROUTE } from 'constant';
import Actions from './actions';
import Servers from './servers';
import EditForm from '../create';

export default ({ match, history }) => {
  const { id: groupId } = match.params;
  const {
    [ACTIONS_TYPES.AUTO_SCALING.GROUP.GET]: group = {},
    [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [],
    [ACTIONS_TYPES.IMAGES.GET_ALL]: images = [],
  } = useSelector((e) => e);

  const { entities = {} } = group;
  const { actions: groupActions = {}, servers: groupServers = {} } = entities;
  const { items: groupActionsItems = [] } = groupActions;
  const { items: groupServersItems = [] } = groupServers;

  useEffect(() => {
    if (!dataCenters.length) actions.dataCenter.getAll({});
    if (isEmptyArray(images)) actions.images.getAll();
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (groupId) actions.autoScaling.group.get({ id: groupId });
  }, [groupId]);

  const locationLabel = ({ location }) => (
    <div className="flex">
      <img style={{ width: '16px', height: '16px', marginRight: '5px' }} src={(locations[location] || {}).image} alt={location} />
      <div style={{ fontSize: 'small' }}>{(locations[location] || {}).name}</div>
    </div>
  );

  const { id: dcId = '', properties: dcProps = {} } = dataCenters.find((item) => item.id === group?.properties?.datacenter.id) || {};

  const getPlanProperties = () => {
    const { properties = {}, id } = group;
    if (properties.replicaConfiguration) {
      if (images.length) {
        properties.replicaConfiguration.volumes = properties.replicaConfiguration.volumes.map((item) => {
          if (typeof item.image === 'string') {
            item.image = images.find((imageItem) => imageItem.id === item.image);
          }
          return item;
        });
      }
    }
    return {
      id,
      ...properties,
    };
  };

  const getPropertiesRows = () => {
    const {
      datacenter = {},
      maxReplicaCount,
      minReplicaCount,
      targetReplicaCount,
      location,
      policy: {
        metric,
        scaleInThreshold,
        scaleOutThreshold,
        range,
        unit,
        scaleInAction: {
          amount: scaleInAmount,
          amountType: scaleInAmountType,
          cooldownPeriod: scaleInCooldownPeriod,
          terminationPolicy,
        } = {},
        scaleOutAction: {
          amount: scaleOutAmount,
          amountType: scaleOutAmountType,
          cooldownPeriod: scaleOutCooldownPeriod,
        } = {},
      } = {},
      replicaConfiguration: {
        cpuFamily,
        ram,
        cores,
        availabilityZone,
        nics = [],
        volumes = [],
      } = {},
    } = getPlanProperties();

    const cpuFamilyObject = CPU_FAMILY[cpuFamily] || {};


    return [
      ['Region', locationLabel({ location })],
      ['Data Center', (
        <span
          className="color-link pointer"
          onClick={() => history.push(history.push(createLink(ROUTE.DASHBOARD.DATA_CENTER, { id: dcId })))}
        >
          <Shared.widgets.Icon name="boxes" style={{ marginRight: 5 }} />
          {dcProps.name || datacenter.id}
        </span>
      )],
      [_t('AUTO_SCALE.GROUPS.FIELDS.targetReplicaCount'), targetReplicaCount],
      [_t('AUTO_SCALE.GROUPS.FIELDS.maxReplicaCount'), maxReplicaCount],
      [_t('AUTO_SCALE.GROUPS.FIELDS.minReplicaCount'), minReplicaCount],
      [<span style={{ color: 'var(--color-base)', fontSize: 18 }}>{_t('AUTO_SCALE.GROUPS.FIELDS.policy')}</span>],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyMetric'), _t(`AUTO_SCALE.GROUPS.OPTIONS.METRIC.${metric}`)],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInThreshold'), scaleInThreshold],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutThreshold'), scaleOutThreshold],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyRange'), range],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyUnit'), _t(`AUTO_SCALE.GROUPS.OPTIONS.UNITS.${unit}`)],
      [
        <span style={{ color: 'var(--color-base)', fontSize: 16 }}>
          {_t('AUTO_SCALE.GROUPS.FIELDS.scaleInAction')}
        </span>,
      ],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmount'), scaleInAmount],
      [
        _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmountType'),
        _t(`AUTO_SCALE.GROUPS.OPTIONS.AMOUNT_TYPES.${scaleInAmountType}`),
      ],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInCooldownPeriod'), scaleInCooldownPeriod],
      [_t('AUTO_SCALE.GROUPS.FIELDS.terminationPolicy'), _t(`AUTO_SCALE.GROUPS.OPTIONS.TERMINATION_POLICIES.${terminationPolicy}`)],
      [
        <span style={{ color: 'var(--color-base)', fontSize: 16 }}>
          {_t('AUTO_SCALE.GROUPS.FIELDS.scaleOutAction')}
        </span>,
      ],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmount'), scaleOutAmount],
      [
        _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmountType'),
        _t(`AUTO_SCALE.GROUPS.OPTIONS.AMOUNT_TYPES.${scaleOutAmountType}`),
      ],
      [_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInCooldownPeriod'), scaleOutCooldownPeriod],
      [<span style={{ color: 'var(--color-base)', fontSize: 18 }}>{_t('AUTO_SCALE.REPLICA_CONFIG')}</span>],
      [_t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.cpuFamily'), (
        <span>
          <img src={cpuFamilyObject?.logo} style={{ width: 24, verticalAlign: 'middle' }} alt="CPU Logo" />
          {` ${cpuFamilyObject?.name}`}
        </span>
      )],
      [_t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.availabilityZone'), availabilityZone?.replace('_', ' ')],
      [_t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.cores'), cores],
      [_t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.ram'), { value: ram / 1024, suffix: 'GB' }],

      [<span style={{ color: 'var(--color-base)', fontSize: 16 }}>NICs</span>],
      [
        <div>
          {
            nics.map((nic, i) => (
              <Shared.widgets.CardBasic
                icon={resourceIconMap(RESOURCE_TYPE.NIC)}
                key={i}
                title={`NIC ${nic.name}`}
                details={(
                  <div>
                    {`LAN: ${nic.lan} DHCP: ${nic.dhcp ? '✓' : '✗'}`}
                  </div>
                )}
              />
            ))
          }
        </div>,
      ],
      [<span style={{ color: 'var(--color-base)', fontSize: 16 }}>{_t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.volumes')}</span>],
      [
        <div>
          {
            volumes.map((volume, i) => (
              <Shared.widgets.CardBasic
                icon={resourceIconMap(RESOURCE_TYPE.VOLUME)}
                key={i}
                title={volume.name}
                details={(
                  <div>
                    <div>
                      {volume.size}
                      GB
                      <span> </span>
                      {volume.type}
                    </div>
                    <div>
                      {volume.image?.name || ''}
                    </div>
                  </div>
                )}
              />
            ))
          }
        </div>,
      ],
    ];
  };

  return (
    <Shared.widgets.Page
      route={ROUTE.DASHBOARD.AUTO_SCALING_GROUP}
      onDelete={() => {
        actions.autoScaling.group.delete({ id: groupId });
        history.push(ROUTE.DASHBOARD.AUTO_SCALING.path);
      }}
      editForm={<EditForm group={getPlanProperties()} />}
      editAction={ACTIONS_TYPES.AUTO_SCALING.GROUP.UPDATE}
      strapTitle={getPlanProperties().name}
    >
      <Shared.widgets.Properties properties={getPropertiesRows()} title="Properties" />
      <Actions groupActions={groupActionsItems.map(({ properties, ...props }) => ({ ...properties, ...props }))} />
      <div className="p-t-20" />
      <Servers groupServers={groupServersItems.map(({ properties, ...props }) => ({ ...properties, ...props }))} />
    </Shared.widgets.Page>
  );
};
